import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Button, Table, Spinner } from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ArrowRight } from "react-bootstrap-icons";
import api from "../../../axiosConfig";
import { popup } from "../../common/popup";

const HistoryToolsDetail = () => {
  const { tool_id } = useParams();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { kode, part_name } = location.state || {};

  const fetchHistory = useCallback(async () => {
    if (!tool_id || tool_id === "undefined") return;

    setLoading(true);
    try {
      const res = await api.get(`/maintenance/tools/history/${tool_id}`);
      setHistory(res.data || []);
    } catch (err) {
      console.error(err);
      popup.error("Gagal mengambil detail history tools");
    } finally {
      setLoading(false);
    }
  }, [tool_id]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filteredHistory = history.filter((h) => {
    if (!dateFrom && !dateTo) return true;

    const startDate = new Date(h.start_time);
    startDate.setHours(0, 0, 0, 0);

    const fromDate = dateFrom ? new Date(dateFrom) : null;
    const toDate = dateTo ? new Date(dateTo) : null;

    if (fromDate) fromDate.setHours(0, 0, 0, 0);
    if (toDate) toDate.setHours(23, 59, 59, 999);

    if (fromDate && startDate < fromDate) return false;
    if (toDate && startDate > toDate) return false;

    return true;
  });

  const downloadExcel = () => {
    if (filteredHistory.length === 0) {
      popup.alert("Tidak ada data untuk didownload");
      return;
    }

    const data = filteredHistory.map((h, i) => ({
      No: i + 1,
      Tanggal: formatDateOnly(h.start_time),
      Status:
        h.action_type === "REPLACE"
          ? `REPLACE → ${h.replaced_kode || "-"}`
          : h.judgment || "-",

      "Part Pengganti": h.replaced_name || "-",
      "Spec Pengganti": h.replaced_spec || "-",

      PIC: h.dikerjakan_oleh,
      Dicek: h.dicek_oleh,
      Mulai: formatTimeOnly(h.start_time),
      Selesai: formatTimeOnly(h.end_time),
      Durasi: calcDuration(h.start_time, h.end_time),
      Kerusakan: h.issue || "-",
      Penyelesaian: h.solution || "-",
      Keterangan: h.parent_id ? "Lanjutan dari NG" : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "History Tool");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileData = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = `History_Tool_${kode || tool_id}_${
      dateFrom || "all"
    }_to_${dateTo || "all"}.xlsx`;

    saveAs(fileData, fileName);
  };

  const formatDateOnly = (d) =>
    d
      ? new Date(d).toLocaleDateString("id-ID", {
          timeZone: "Asia/Jakarta",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "-";

  const formatTimeOnly = (d) =>
    d
      ? new Date(d).toLocaleTimeString("id-ID", {
          timeZone: "Asia/Jakarta",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "-";

  const calcDuration = (from, to) => {
    if (!from || !to) return "-";
    const diffMs = new Date(to) - new Date(from);
    if (diffMs <= 0) return "-";

    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return hours > 0
      ? `${hours} j ${remainingMinutes} m`
      : `${minutes} m ${seconds} d`;
  };

  const renderStatusBadge = (h) => {
    let variant = "secondary";
    let label = "-";

    if (h.action_type === "REPLACE" || h.replaced_kode) {
      variant = "info";
      label = "REPLACE";
    } else if (h.judgment === "NG") {
      variant = "danger";
      label = "NG";
    } else if (h.judgment === "OK") {
      variant = "success";
      label = "OK";
    }

    return (
      <span className={`badge bg-${variant}`} style={{ fontSize: "13px" }}>
        {label}
      </span>
    );
  };

  return (
    <>
      <div className="d-flex" style={{ gap: "22%" }}>
        <div style={{ gap: "5px" }}>
          <h5 style={{ fontSize: "30px", marginBottom: "4px" }}>
            HISTORY TOOL
          </h5>
          <div style={{ fontSize: "24px", fontWeight: "500" }}>
            {kode ? `${kode} — ${part_name}` : "Detail Tool"}
          </div>
        </div>
        <div className=" align-items-end">
          <div className="d-flex mb-3 align-items-end" style={{ gap: "15px" }}>
            <div>
              <label>Dari Tanggal</label>
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>

            <div>
              <label>Sampai Tanggal</label>
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>

            <Button
              variant="danger"
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
            >
              Reset
            </Button>
            <Button variant="success" onClick={downloadExcel}>
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      <Table bordered hover responsive>
        <thead>
          <tr>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>No</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Tanggal</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Status</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>PIC</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Dicek</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Mulai</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Selesai</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Durasi</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Kerusakan</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>
              Gejala Masalah
            </th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>Penyebab</th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>
              Evaluasi Perbaikan
            </th>
            <th style={{ background: "#1e3a8a", color: "#fff" }}>
              Penyelesaian
            </th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan={13} className="text-center">
                <Spinner size="sm" /> Loading...
              </td>
            </tr>
          ) : filteredHistory.length === 0 ? (
            <tr>
              <td colSpan={13} className="text-center">
                Belum ada history
              </td>
            </tr>
          ) : (
            filteredHistory.map((h, i) => (
              <tr
                key={h.id}
                style={
                  h.action_type === "REPLACE"
                    ? { backgroundColor: "#f8f9fa" }
                    : {}
                }
              >
                <td className="text-center">
                  {h.action_type === "REPLACE" && h.parent_id
                    ? `${i}.1`
                    : i + 1}
                </td>
                <td>{formatDateOnly(h.start_time)}</td>
                <td>
                  {renderStatusBadge(h)}

                  {h.action_type === "REPLACE" && h.replaced_name && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#555",
                        marginTop: "6px",
                        lineHeight: "1.4",
                      }}
                    >
                      <div>
                        <ArrowRight /> <b>Penggantian Tools</b>
                      </div>

                      <div style={{ marginTop: "4px" }}>
                        <b>{h.replaced_name}</b>
                      </div>

                      <div style={{ fontSize: "12px", color: "#666" }}>
                        Spec: {h.replaced_spec || "-"}
                      </div>

                      <div style={{ fontSize: "12px", color: "#888" }}>
                        Category: {h.replaced_category || "-"}
                      </div>

                      {h.parent_id && (
                        <div
                          style={{
                            fontStyle: "italic",
                            color: "#999",
                            marginTop: "2px",
                          }}
                        >
                          lanjutan dari NG
                        </div>
                      )}
                    </div>
                  )}
                </td>

                <td>{h.dikerjakan_oleh}</td>
                <td>{h.dicek_oleh}</td>
                <td>{formatTimeOnly(h.start_time)}</td>
                <td>{formatTimeOnly(h.end_time)}</td>
                <td>{calcDuration(h.start_time, h.end_time)}</td>
                <td className="text-start">{h.issue || "-"}</td>
                <td className="text-start">{h.gejala_masalah || "-"}</td>
                <td className="text-start">{h.penyebab_masalah || "-"}</td>
                <td className="text-start">{h.evaluasi_perbaikan || "-"}</td>
                <td className="text-start">{h.solution || "-"}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </>
  );
};

export default HistoryToolsDetail;
